import { Box, Divider, ListItemIcon, ListItemText, MenuItem, MenuList, Popover, PopoverProps, Theme, Typography, useTheme } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useContext, useLayoutEffect, useState } from 'react'
import RootStore from '../stores/RootStore'
import StyledIcon from './StyledIcon'
import { isLast } from '../../shared-lib/utils'
import { $ActionMenuData } from '../stores/UIStore/UIStore'

export const ActionMenuForUIStore = observer(() => {
  const store = useContext(RootStore)
  const { actionMenuVisible, actionMenuData, closeActionMenu } = store.uiStore

  return <ActionMenu
    actionMenuVisible={actionMenuVisible}
    actionMenuData={actionMenuData}
    anchorOrigin={actionMenuData.anchorOrigin}
    transformOrigin={actionMenuData.transformOrigin}
    closeActionMenu={closeActionMenu}
    disablePortal={false}
    positionAdjustment={{ topGrid: -0.5, leftGrid: 1.5 }}
  />
})

interface Props {
  actionMenuVisible: boolean
  actionMenuData: $ActionMenuData
  closeActionMenu: () => void
  disablePortal?: boolean
  anchorOrigin?: PopoverProps['anchorOrigin']
  transformOrigin?: PopoverProps['transformOrigin']
  positionAdjustment?: {
    topGrid: number
    leftGrid: number
  }
}

const ActionMenu = (props: Props) => {
  const {
    actionMenuVisible,
    actionMenuData,
    closeActionMenu,
    anchorOrigin = { vertical: 'top', horizontal: 'right' },
    transformOrigin = { vertical: 'top', horizontal: 'right' },
    positionAdjustment = { topGrid: 0, leftGrid: 0 },
  } = props

  const theme = useTheme()

  const { desktopAnchorEl } = actionMenuData
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  useLayoutEffect(() => {
    setAnchorEl(desktopAnchorEl ? document.getElementById(desktopAnchorEl) : null)
  }, [desktopAnchorEl, actionMenuVisible])

  const zIndexStyle = actionMenuData.zIndex !== undefined ? { zIndex: actionMenuData.zIndex } : {}

  const getColor = (color: 'primary' | 'danger' | undefined) => {
    if (color === 'danger') return 'error'
    return 'darkGray'
  }

  return <Popover
    anchorEl={anchorEl}
    open={actionMenuVisible}
    onClose={closeActionMenu}
    anchorOrigin={anchorOrigin}
    transformOrigin={transformOrigin}
    elevation={10}
    sx={{
      ...zIndexStyle,
      '& .MuiPopover-paper': {
        marginTop: theme.spacing(positionAdjustment.topGrid),
        marginLeft: theme.spacing(positionAdjustment.leftGrid),
      },
    }}
  >
    <MenuList sx={{ py: 1 }}>
      {actionMenuData.title && <Box>
        <Typography fontWeight='medium' sx={{ px: 3, py: 2 }}>{actionMenuData.title}</Typography>
        <Divider />
      </Box>}

      {actionMenuData.actions?.map((action) => {
        return <MenuItem
          disabled={action.disabled}
          key={action.title}
          divider={!isLast(actionMenuData.actions!, action)}
          onClick={() => {
            action.action()
            closeActionMenu()
          }}
          disableRipple
          sx={{
            paddingTop: 2,
            paddingBottom: 2,
            paddingRight: 4,
            minWidth: theme.spacing(50),
            transition: 'all 0.3s ease-in-out',
            '&.MuiButtonBase-root:hover': {
              transition: 'none',
              '@media (hover: hover)': { // This only applies the hover styles if :hover is supported, e.g. not on touch devices
                backgroundColor: (theme: Theme) => theme.palette.secondary.light,
              },
            },
            '&:active.MuiButtonBase-root': {
              transition: 'none',
              backgroundColor: (theme: Theme) => theme.palette.secondary.main,
            },
          }}
        >
          {action.icon && <ListItemIcon sx={{ minWidth: 0, pr: 2 }}>
            <StyledIcon name={action.icon} color={getColor(action.color)} />
          </ListItemIcon>}
          <ListItemText primaryTypographyProps={{ color: getColor(action.color) }}>{action.title}</ListItemText>
        </MenuItem>
      })}
    </MenuList>

  </Popover>
}

export default observer(ActionMenu)